<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, onMounted, watch, nextTick } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            zoom: "Zoom",
            rotate: "Rotation",
            cancel: "Annuler",
            validate: "Valider",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * URL de l'image à éditer (base64 ou URL)
     */
    image: string
    /**
     * Indique si le composant est en chargement
     */
    loading?: boolean
}

type Emits = {
    /**
     * Émis quand l'utilisateur annule l'édition
     */
    "click:cancel": []
    /**
     * Émis quand l'utilisateur valide l'édition
     */
    "click:submit": [data: { file: File; cropImage: HTMLCanvasElement }]
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    loading: false,
})

const emit = defineEmits<Emits>()

// ================================
// STATE
// ================================

/** Référence au canvas d'édition */
const canvasRef = ref<HTMLCanvasElement | null>(null)
/** Niveau de zoom (0.5 à 2) */
const zoom = ref(1)
/** Angle de rotation en degrés (-180 à 180) */
const rotation = ref(0)
/** Indique si l'image est chargée */
const imageLoaded = ref(false)
/** Image en cache pour le redessin */
const cachedImage = ref<HTMLImageElement | null>(null)

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Dessine l'image sur le canvas avec les transformations actuelles
 * @param img - Image à dessiner
 * @param zoomValue - Niveau de zoom à appliquer
 * @param rotationValue - Angle de rotation en degrés
 */
function drawImageOnCanvas(
    img: HTMLImageElement,
    zoomValue: number,
    rotationValue: number
): void {
    if (!canvasRef.value) return

    const canvas = canvasRef.value
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Calcule les dimensions pour un crop circulaire de 200x200
    const size = 200
    canvas.width = size
    canvas.height = size

    // Efface le canvas pour éviter les résidus au changement de zoom/rotation
    ctx.clearRect(0, 0, size, size)

    // Calcule le ratio pour remplir le canvas à zoom 1 (cover)
    const imgRatio = img.width / img.height
    let drawWidth = size
    let drawHeight = size

    if (imgRatio > 1) {
        drawHeight = size / imgRatio
    } else {
        drawWidth = size * imgRatio
    }

    // Offsets pour centrer l'image (taille "cover" à zoom 1)
    const offsetX = size / 2 - drawWidth / 2
    const offsetY = size / 2 - drawHeight / 2

    // Applique les transformations (centre = centre du canvas)
    // scale(zoom) zoome vraiment : zoom > 1 = zoom avant, zoom < 1 = dézoom
    ctx.save()
    ctx.translate(size / 2, size / 2)
    ctx.rotate((rotationValue * Math.PI) / 180)
    ctx.scale(zoomValue, zoomValue)
    ctx.translate(-size / 2, -size / 2)

    // Dessine l'image en taille cover ; le scale(zoom) ci-dessus applique le zoom
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)

    ctx.restore()

    // Masque circulaire en coordonnées écran (sans transformation)
    // pour éviter que le cercle soit déformé par le zoom/rotation
    ctx.globalCompositeOperation = "destination-in"
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI)
    ctx.fill()
}

/**
 * Charge l'image et la met en cache
 */
function loadImage(): void {
    if (!props.image) return

    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onerror = () => {
        console.error("Erreur lors du chargement de l'image")
        imageLoaded.value = false
        cachedImage.value = null
    }
    img.onload = () => {
        cachedImage.value = img
        imageLoaded.value = true
        drawImageOnCanvas(img, zoom.value, rotation.value)
    }
    img.src = props.image
}

/**
 * Met à jour le canvas avec les transformations actuelles
 */
function updateCanvas(): void {
    if (!imageLoaded.value || !cachedImage.value) return
    drawImageOnCanvas(cachedImage.value, zoom.value, rotation.value)
}

// ================================
// INTERACTIONS
// ================================

/**
 * Annule l'édition et ferme le composant
 */
function onCancelClick(): void {
    emit("click:cancel")
}

/**
 * Valide l'édition et émet l'image recadrée
 */
function onClickSubmit(): void {
    if (!canvasRef.value) return

    const canvas = canvasRef.value
    canvas.toBlob(
        (blob) => {
            if (blob) {
                const file = new File([blob], "company-logo.png", {
                    type: "image/png",
                })
                emit("click:submit", { file, cropImage: canvas })
            }
        },
        "image/png",
        0.95
    )
}

// ================================
// WATCHERS
// ================================

onMounted(() => {
    loadImage()
})

watch(
    () => props.image,
    () => {
        imageLoaded.value = false
        cachedImage.value = null
        loadImage()
    }
)

/** Redessine le canvas quand zoom ou rotation changent */
watch(
    [zoom, rotation],
    () => {
        nextTick(() => updateCanvas())
    },
    { flush: "post" }
)
</script>

<template>
    <div class="w-full max-w-[28rem] mx-auto flex flex-col gap-4">
        <!-- Canvas pour l'édition -->
        <div
            class="w-full flex justify-center items-center min-h-[14.625rem] bg-gray-100 rounded-lg p-4"
        >
            <canvas
                ref="canvasRef"
                class="rounded-full shadow-md"
                :width="200"
                :height="200"
            ></canvas>
        </div>

        <!-- Contrôles d'édition -->
        <div class="w-full flex flex-col gap-4">
            <!-- Zoom -->
            <div>
                <BaseInputWrapper input-id="zoom" :label="t('zoom')" unfloat>
                    <input
                        id="zoom"
                        v-model.number="zoom"
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        class="w-full"
                    />
                </BaseInputWrapper>
            </div>

            <!-- Rotation -->
            <div>
                <BaseInputWrapper
                    input-id="rotation"
                    :label="t('rotate')"
                    unfloat
                >
                    <input
                        id="rotation"
                        v-model.number="rotation"
                        type="range"
                        min="-180"
                        max="180"
                        step="1"
                        class="w-full"
                    />
                </BaseInputWrapper>
            </div>

            <!-- Boutons d'action -->
            <div class="flex gap-2 justify-end">
                <BaseButton
                    :label="t('cancel')"
                    severity="secondary"
                    text
                    :disabled="loading"
                    @click="onCancelClick"
                />
                <BaseButton
                    :label="t('validate')"
                    :loading="loading"
                    :disabled="loading"
                    @click="onClickSubmit"
                />
            </div>
        </div>
    </div>
</template>
